// AchievementsView.tsx
import React, { useMemo, useRef, useState, useEffect } from 'react';
import RoddoPageShell from './layout/RoddoPageShell';
import RoddoSectionCard from './layout/RoddoSectionCard';
import { achievementCatalog } from '../constants/challenges';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import { buildReputationSummary, extractTrackValuesFromSummary } from '../utils/reputation';
import { xpProgress } from '../utils/xp';
import type { UserProfile } from '../types';
import type { BackendAchievement } from '../services/challengeEngine';

type AchievementCategory = 'exploration' | 'contribution' | 'consistency' | 'mastery';
type TrackKey =
  | 'species_mastery'
  | 'explorer'
  | 'community_contributor'
  | 'bazaar_trader'
  | 'tournament_competitor';

interface Props {
  userProfile: UserProfile;
  achievementMap?: Record<string, BackendAchievement>;
  isDarkMode?: boolean;
  onBack?: () => void;
}

const CATEGORY_LABELS: Record<AchievementCategory, string> = {
  exploration: 'Exploration',
  contribution: 'Contribution',
  consistency: 'Consistency',
  mastery: 'Mastery',
};

const CATEGORY_ORDER: AchievementCategory[] = ['exploration', 'contribution', 'consistency', 'mastery'];

function iconForAchievement(entry: any): string {
  const text = `${String(entry?.title ?? '')} ${String(entry?.description ?? '')} ${String(entry?.category ?? '')}`.toLowerCase();
  if (text.includes('first catch') || text.includes('catch')) return '🐟';
  if (text.includes('species')) return '🐠';
  if (text.includes('water') || text.includes('explor') || text.includes('map')) return '🗺️';
  if (text.includes('community') || text.includes('comment') || text.includes('share') || text.includes('contribution')) return '💬';
  if (text.includes('market') || text.includes('bazaar') || text.includes('trade') || text.includes('sell') || text.includes('buy')) return '🛍️';
  if (text.includes('tournament') || text.includes('champ') || text.includes('win')) return '🏆';
  if (text.includes('streak') || text.includes('consisten') || text.includes('daily')) return '🔥';
  if (text.includes('master')) return '👑';
  return '✨';
}

function iconToneForAchievement(icon: string) {
  switch (icon) {
    case '🐟':
    case '🐠':
      return { bg: 'rgba(124,240,196,0.16)', fg: '#DFF6EA', lightBg: 'rgba(124,240,196,0.18)', lightFg: '#145C42' };
    case '🗺️':
      return { bg: 'rgba(124,199,255,0.16)', fg: '#DFF1FF', lightBg: 'rgba(124,199,255,0.18)', lightFg: '#0E5B85' };
    case '💬':
      return { bg: 'rgba(255,159,176,0.16)', fg: '#FFF0F3', lightBg: 'rgba(255,159,176,0.18)', lightFg: '#8B2F47' };
    case '🛍️':
      return { bg: 'rgba(246,196,83,0.16)', fg: '#FFE8A3', lightBg: 'rgba(246,196,83,0.20)', lightFg: '#775700' };
    case '🏆':
    case '👑':
      return { bg: 'rgba(196,181,253,0.16)', fg: '#EFE3FF', lightBg: 'rgba(196,181,253,0.24)', lightFg: '#5B2E91' };
    case '🔥':
      return { bg: 'rgba(255,181,107,0.18)', fg: '#FFF0D9', lightBg: 'rgba(255,181,107,0.20)', lightFg: '#8A4B00' };
    default:
      return { bg: 'rgba(255,255,255,0.08)', fg: '#F3F6FF', lightBg: 'rgba(255,255,255,0.74)', lightFg: '#544C43' };
  }
}

const TRACKS: Array<{
  key: TrackKey;
  title: string;
  description: string;
  accent: 'mint' | 'sky' | 'coral' | 'gold' | 'plum';
  icon: string;
}> = [
  { key: 'species_mastery', title: 'Species Mastery', description: 'Unique fish species logged', accent: 'mint', icon: '🐟' },
  { key: 'explorer', title: 'Explorer', description: 'Unique waters discovered', accent: 'sky', icon: '🗺️' },
  { key: 'community_contributor', title: 'Community Contributor', description: 'Shared fishing signals and community wins', accent: 'coral', icon: '💬' },
  { key: 'bazaar_trader', title: 'Bazaar Trader', description: 'Marketplace trust and movement', accent: 'gold', icon: '🛍️' },
  { key: 'tournament_competitor', title: 'Tournament Competitor', description: 'Events entered and pressure handled', accent: 'plum', icon: '🏆' },
];

function normalizeAchievements(source: any[]): Array<any> {
  return (Array.isArray(source) ? source : [])
    .map((entry, index) => ({
      id: String(entry?.id ?? entry?.key ?? index),
      title: String(entry?.title ?? entry?.name ?? 'Achievement'),
      description: String(entry?.description ?? entry?.summary ?? ''),
      category: String(entry?.category ?? 'mastery').toLowerCase() as AchievementCategory,
      tier: String(entry?.tier ?? entry?.level ?? 'Unlocked'),
      unlocked: Boolean(entry?.unlocked ?? entry?.earned ?? entry?.completed ?? entry?.isUnlocked),
      progress: Number(entry?.progress ?? 0),
      target: Number(entry?.target ?? entry?.goal ?? 0),
      rewardXP: Number(entry?.rewardXP ?? entry?.xp ?? entry?.points ?? 0),
      unlockedAt: Number(entry?.unlockedAt ?? entry?.completedAt ?? 0),
    }))
    .filter((entry) => entry.title);
}

const toneForAccent = (accent: string) => {
  switch (accent) {
    case 'mint':
      return {
        chipBg: 'rgba(134,239,172,0.22)',
        chipText: '#145C42',
        darkChipBg: 'rgba(134,239,172,0.16)',
        darkChipText: '#DFF6EA',
        bar: '#34D399',
        glow: 'rgba(124,240,196,0.20)',
        dot: '#7CF0C4',
      };
    case 'sky':
      return {
        chipBg: 'rgba(124,199,255,0.22)',
        chipText: '#0E5B85',
        darkChipBg: 'rgba(124,199,255,0.16)',
        darkChipText: '#DFF1FF',
        bar: '#2F80ED',
        glow: 'rgba(124,199,255,0.22)',
        dot: '#7CC7FF',
      };
    case 'coral':
      return {
        chipBg: 'rgba(255,159,176,0.22)',
        chipText: '#8B2F47',
        darkChipBg: 'rgba(255,159,176,0.16)',
        darkChipText: '#FFF0F3',
        bar: '#EF8B91',
        glow: 'rgba(255,159,176,0.22)',
        dot: '#FF9FB0',
      };
    case 'gold':
      return {
        chipBg: 'rgba(246,196,83,0.24)',
        chipText: '#775700',
        darkChipBg: 'rgba(246,196,83,0.16)',
        darkChipText: '#FFE8A3',
        bar: '#F2B84B',
        glow: 'rgba(246,196,83,0.22)',
        dot: '#F6C453',
      };
    case 'plum':
    default:
      return {
        chipBg: 'rgba(196,181,253,0.28)',
        chipText: '#5B2E91',
        darkChipBg: 'rgba(196,181,253,0.16)',
        darkChipText: '#EFE3FF',
        bar: '#7057E8',
        glow: 'rgba(196,181,253,0.24)',
        dot: '#C4B5FD',
      };
  }
};


function normalizeAchievementKey(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/__all_time$/, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function inferAchievementProgress(entry: any, userProfile: UserProfile, unlocked: boolean) {
  const stats: any = (userProfile as any)?.stats || {};
  const catches = Number(stats?.catches ?? (userProfile as any)?.totalCatches ?? (userProfile as any)?.log?.length ?? 0);
  const species = Number(stats?.speciesMasteryCount ?? stats?.uniqueSpeciesCount ?? stats?.uniqueSpecies ?? stats?.species ?? 0);
  const waters = Number(stats?.waters ?? stats?.uniqueWatersCount ?? stats?.spots ?? 0);
  const regions = Number(stats?.regions ?? stats?.uniqueRegionsCount ?? 0);
  const techniques = Number(stats?.uniqueTechniquesCount ?? stats?.techniques ?? 0);
  const shared = Number(stats?.sharedContributions ?? stats?.communityPosts ?? stats?.posts ?? 0);
  const key = normalizeAchievementKey(entry?.id ?? entry?.key ?? entry?.title);

  let progress = 0;
  let target = 1;

  switch (key) {
    case 'first_catch':
      progress = catches;
      break;
    case 'first_species':
      progress = species;
      break;
    case 'first_water':
      progress = waters;
      break;
    case 'first_region':
      progress = regions;
      break;
    case 'first_technique':
      progress = techniques;
      break;
    case 'first_shared_contribution':
      progress = shared;
      break;
    default:
      progress = unlocked ? 1 : 0;
      target = unlocked ? 1 : 0;
      break;
  }

  return {
    progress: Math.max(0, Number(progress || 0)),
    target: Math.max(unlocked ? 1 : target, Number(target || 0)),
  };
}

const categoryAccent = (category: AchievementCategory | 'all') => {
  switch (category) {
    case 'exploration':
      return { dot: '#7CDAFF', rail: '#7CC7FF', glow: 'rgba(124,199,255,0.24)' };
    case 'contribution':
      return { dot: '#FFB4C5', rail: '#FF9FB0', glow: 'rgba(255,159,176,0.22)' };
    case 'consistency':
      return { dot: '#E7F07C', rail: '#CFE15A', glow: 'rgba(231,240,124,0.20)' };
    case 'mastery':
      return { dot: '#D4B3FF', rail: '#C4B5FD', glow: 'rgba(196,181,253,0.24)' };
    case 'all':
    default:
      return { dot: '#9278EC', rail: '#7657E8', glow: 'rgba(155,124,255,0.24)' };
  }
};

const AchievementsView: React.FC<Props> = ({ userProfile, achievementMap, isDarkMode: isDarkModeProp, onBack }) => {
  const [selectedCategory, setSelectedCategory] = useState<AchievementCategory | 'all'>('all');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [activeTrack, setActiveTrack] = useState<TrackKey | null>('species_mastery');
  const menuRef = useRef<HTMLDivElement | null>(null);

  const isDarkMode = resolveRoddoDarkMode(isDarkModeProp);
  const ui = getRoddoUiTokens(isDarkMode);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setIsMenuOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  const shellStyle: React.CSSProperties = {};
  const softCardStyle: React.CSSProperties = {
    background: isDarkMode ? '#101827' : ui.cardBg,
    border: `1px solid ${ui.border}`,
    boxShadow: isDarkMode ? '0 6px 16px rgba(0,0,0,0.14)' : '0 8px 20px rgba(89,65,37,0.07), inset 0 1px 0 rgba(255,255,255,0.56)',
  };
  const mutedCardStyle: React.CSSProperties = {
    background: isDarkMode ? '#0F1828' : ui.cardBgMuted,
    border: `1px solid ${ui.border}`,
    boxShadow: isDarkMode ? 'none' : '0 5px 14px rgba(89,65,37,0.05), inset 0 1px 0 rgba(255,255,255,0.48)',
  };
  const textPrimary = { color: ui.textPrimary };
  const textSecondary = { color: isDarkMode ? ui.textSecondary : '#5F564C' };
  const textMuted = { color: isDarkMode ? ui.textMuted : '#746B61' };

  const reputationSummary = useMemo(() => {
    const existing = (userProfile as any)?.reputation;
    return existing?.tracks ? existing : buildReputationSummary({});
  }, [userProfile]);

  const trackValues = useMemo(() => extractTrackValuesFromSummary(reputationSummary) ?? {}, [reputationSummary]);
  const xpInfo = useMemo(() => xpProgress(Number((userProfile as any)?.xp ?? 0)), [userProfile]);
  const unlockedAchievementKeys = useMemo(() => {
    const keys = new Set<string>();
    Object.keys(achievementMap || {}).forEach((key) => keys.add(normalizeAchievementKey(key)));
    (((userProfile as any)?.achievements || []) as any[]).forEach((entry) => {
      keys.add(normalizeAchievementKey(entry?.key ?? entry?.name ?? entry?.title));
    });
    return keys;
  }, [achievementMap, userProfile]);
  const normalizedAchievements = useMemo(() => {
    const backendRows = Object.values(achievementMap || {}) as any[];
    const catalogRows = normalizeAchievements(achievementCatalog as any[]);
    const seen = new Set<string>();

    const fromCatalog = catalogRows.map((entry) => {
      const key = normalizeAchievementKey(entry.id || entry.title);
      seen.add(key);
      const backend = backendRows.find(
        (row: any) => normalizeAchievementKey(row?.key || row?.id || row?.title) === key,
      );
      const unlocked = !!backend || unlockedAchievementKeys.has(key) || !!entry.unlocked;
      const inferred = inferAchievementProgress(entry, userProfile, unlocked);
      return {
        ...entry,
        unlocked,
        progress: Number(backend?.unlockedAtMs ? inferred.target || 1 : inferred.progress),
        target: inferred.target,
        unlockedAt: Number(backend?.unlockedAtMs ?? entry.unlockedAt ?? 0),
        rewardXP: Number(backend?.rewardXp ?? entry.rewardXP ?? 0),
      };
    });

    const dynamicBackend = backendRows
      .map((row: any) => {
        const key = normalizeAchievementKey(row?.key || row?.id || row?.title);
        if (!key || seen.has(key)) return null;
        seen.add(key);
        return {
          id: String(row?.key || row?.id || key),
          title: String(row?.title || 'Achievement'),
          description: String(row?.description || ''),
          category: String(row?.category || 'mastery').toLowerCase() as AchievementCategory,
          tier: String(row?.tier || row?.reputationTier || 'Unlocked'),
          unlocked: true,
          progress: 1,
          target: 1,
          rewardXP: Number(row?.rewardXp ?? row?.rewardXP ?? 0),
          unlockedAt: Number(row?.unlockedAtMs ?? row?.completedAtMs ?? 0),
        };
      })
      .filter(Boolean) as any[];

    return [...dynamicBackend, ...fromCatalog];
  }, [achievementMap, unlockedAchievementKeys, userProfile]);
  const unlockedCount = normalizedAchievements.filter((entry) => entry.unlocked).length;

  const grouped = useMemo(() => {
    const base: Record<AchievementCategory, any[]> = {
      exploration: [],
      contribution: [],
      consistency: [],
      mastery: [],
    };

    normalizedAchievements.forEach((entry) => {
      const category = entry.category as AchievementCategory;
      const key: AchievementCategory = CATEGORY_ORDER.includes(category) ? category : 'mastery';
      base[key].push(entry);
    });

    return base;
  }, [normalizedAchievements]);

  const visibleEntries = useMemo(() => {
    if (selectedCategory === 'all') return normalizedAchievements;
    return grouped[selectedCategory] || [];
  }, [grouped, normalizedAchievements, selectedCategory]);

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const categoryMeta = categoryAccent(selectedCategory);
  const trackPillRailStyle: React.CSSProperties = {
    background: isDarkMode ? '#0C1423' : 'rgba(255,252,246,0.72)',
    border: `1px solid ${ui.border}`,
    boxShadow: isDarkMode ? 'inset 0 1px 0 rgba(255,255,255,0.06)' : 'inset 0 1px 0 rgba(255,255,255,0.70)',
  };
  const handleSmartBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    // Safe in-app fallback only. Never use browser history here because it can escape
    // the mounted app shell back to the public roddo.app landing route.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('roddo:navigate-back'));
    }
  };

  return (
    <RoddoPageShell padding="none" contentClassName="max-w-4xl px-0 pt-3 sm:pt-4 roddo-progress-lane">
      <div key={isDarkMode ? 'dark' : 'light'} className="space-y-3 roddo-achievements-board roddo-progress-board" data-roddo-polish-lane="2" style={shellStyle}>
        <div className="overflow-hidden rounded-[18px] p-3.5 sm:p-4" style={mutedCardStyle}>
        <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11px] font-black uppercase tracking-[0.22em]" style={textMuted}>
                  Reputation tracks
                </div>
                <button
                  type="button"
                  onClick={handleSmartBack}
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full transition hover:-translate-y-0.5 active:translate-y-px"
                  style={{
                    background: ui.overlaySoft,
                    color: isDarkMode ? '#F8FAFF' : '#2F3D45',
                    border: `1px solid ${ui.border}`,
                    boxShadow: isDarkMode ? '0 10px 22px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.10)' : '0 10px 20px rgba(89,65,37,0.10), inset 0 1px 0 rgba(255,255,255,0.82)',
                  }}
                  aria-label="Back"
                >
                  <span className="text-xl font-black leading-none">‹</span>
                </button>
              </div>

              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h1 className="min-w-0 text-[15px] font-black leading-6 sm:text-[16px]" style={textPrimary}>
                    What Roddo sees in your run
                  </h1>

                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                    <div
                      className="rounded-[12px] px-3 py-2 text-[11px] font-black uppercase tracking-[0.16em]"
                      style={{
                        background: ui.cardBgMuted,
                        color: isDarkMode ? '#F2F5FF' : '#433A92',
                        border: `1px solid ${ui.border}`,
                      }}
                    >
                      {unlockedCount} unlocked
                    </div>
                    <div
                      className="rounded-[12px] px-3 py-2 text-[11px] font-black uppercase tracking-[0.16em]"
                      style={{
                        background: isDarkMode ? 'rgba(146,120,236,0.12)' : 'rgba(124,199,255,0.12)',
                        color: isDarkMode ? '#E8E0FF' : '#2C578D',
                        border: `1px solid ${ui.border}`,
                      }}
                    >
                      Lv {xpInfo.level} · {xpInfo.totalXP} XP
                    </div>
                  </div>
                </div>

                <p className="max-w-none text-[12px] leading-5 sm:text-[13px]" style={textSecondary}>
                  Your mastery profile across species, exploration, community, bazaar, and tournament pressure.
                </p>
              </div>

              <div className="rounded-[16px] px-3.5 py-3" style={{
                background: ui.cardBg,
                border: 'none',
                boxShadow: isDarkMode
                  ? '0 4px 12px rgba(3,0,16,0.10)'
                  : '0 3px 10px rgba(89,65,37,0.04)',
              }}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.16em]" style={textMuted}>Angler momentum</div>
                    <div className="mt-1 text-[15px] font-black" style={textPrimary}>Level {xpInfo.level} · {xpInfo.rank}</div>
                    <div className="mt-1 text-[11px] font-bold" style={textSecondary}>{unlockedCount} badges earned</div>
                  </div>
                  <div className="text-[12px] font-bold" style={textSecondary}>{xpInfo.remaining} XP to next level</div>
                </div>
                <div className="mt-2.5 h-2.5 overflow-hidden rounded-full" style={{ background: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(91,71,33,0.10)', border: `1px solid ${ui.border}` }}>
                  <div className="h-full rounded-full" style={{ width: `${xpInfo.percent}%`, background: isDarkMode ? '#8A70F2' : '#2F80ED' }} />
                </div>
              </div>

              <div
                className="flex gap-2 overflow-x-auto rounded-[16px] p-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                style={trackPillRailStyle}
              >
                {TRACKS.map((track) => {
                  const tone = toneForAccent(track.accent);
                  const value = Number((trackValues as any)?.[track.key] ?? 0);
                  const isActive = activeTrack === track.key;
                  return (
                    <button
                      key={`${track.key}-pill`}
                      type="button"
                      onClick={() => setActiveTrack((prev) => (prev === track.key ? null : track.key))}
                      className="min-w-[108px] rounded-[11px] px-3 py-2.5 text-left transition sm:min-w-[132px]"
                      style={{
                        background: isActive
                          ? isDarkMode
                            ? 'rgba(112,87,232,0.14)'
                            : 'rgba(47,128,237,0.08)'
                          : 'transparent',
                        border: `1px solid ${isActive ? ui.borderStrong : 'transparent'}`,
                        boxShadow: isActive ? 'inset 0 1px 0 rgba(255,255,255,0.35)' : 'none',
                      }}
                      aria-expanded={isActive}
                      aria-controls={`track-panel-${track.key}`}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="grid h-8 w-8 shrink-0 place-items-center rounded-[11px] text-[15px]"
                          style={{
                            background: isDarkMode ? tone.darkChipBg : tone.chipBg,
                            color: isDarkMode ? tone.darkChipText : tone.chipText,
                            border: `1px solid ${ui.border}`,
                          }}
                          title={track.title}
                        >
                          {track.icon}
                        </span>
                        <div className="min-w-0">
                          <div className="truncate text-[10px] font-black uppercase tracking-[0.10em]" style={textMuted}>
                            {track.title.replace('Community ', '').replace('Tournament ', '')}
                          </div>
                          <div className="mt-0.5 text-[17px] font-black leading-none" style={textPrimary}>
                            {value}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {activeTrack ? (() => {
                const track = TRACKS.find((entry) => entry.key === activeTrack);
                if (!track) return null;
                const tone = toneForAccent(track.accent);
                const value = Number((trackValues as any)?.[track.key] ?? 0);
                const fill = Math.max(6, Math.min(100, value === 0 ? 6 : value * 10));

                return (
                  <div
                    id={`track-panel-${track.key}`}
                    className="overflow-hidden rounded-[16px] transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-px"
                    style={{
                      background: ui.cardBgMuted,
                      border: 'none',
                      boxShadow: isDarkMode ? '0 5px 14px rgba(0,0,0,.14)' : '0 4px 12px rgba(89,65,37,.05)',
                    }}
                  >
                    <div className="flex gap-0">
                      <div
                        className="w-[4px]"
                        style={{
                          background: tone.bar,
                          boxShadow: 'none',
                        }}
                      />
                      <div className="min-w-0 flex-1 px-4 py-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex flex-1 items-start gap-3">
                            <span
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] text-[20px]"
                              style={{
                                background: isDarkMode ? tone.darkChipBg : tone.chipBg,
                                color: isDarkMode ? tone.darkChipText : tone.chipText,
                                border: `1px solid ${ui.border}`,
                              }}
                              aria-hidden="true"
                            >
                              {track.icon}
                            </span>

                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span
                                  className="inline-flex rounded-[11px] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em]"
                                  style={{
                                    background: isDarkMode ? tone.darkChipBg : tone.chipBg,
                                    color: isDarkMode ? tone.darkChipText : tone.chipText,
                                  }}
                                >
                                  {track.title}
                                </span>
                              </div>
                              <p className="mt-2 text-[13px] font-semibold leading-6 sm:text-[14px]" style={textSecondary}>
                                {track.description}
                              </p>
                            </div>
                          </div>

                          <div
                            className="shrink-0 rounded-[12px] px-3 py-1.5 text-sm font-black"
                            style={{
                              background: ui.cardBg,
                              color: isDarkMode ? '#F8FAFF' : '#534B42',
                              border: `1px solid ${ui.border}`,
                            }}
                          >
                            {value}
                          </div>
                        </div>

                        <div className="mt-4 grid gap-2 sm:grid-cols-3">
                          <div className="rounded-[12px] px-3 py-2" style={{ background: ui.cardBg, border: 'none' }}>
                            <div className="text-[10px] font-black uppercase tracking-[0.14em]" style={textMuted}>Track value</div>
                            <div className="mt-1 text-sm font-black" style={textPrimary}>{value}</div>
                          </div>
                          <div className="rounded-[12px] px-3 py-2" style={{ background: ui.cardBg, border: 'none' }}>
                            <div className="text-[10px] font-black uppercase tracking-[0.14em]" style={textMuted}>Summary</div>
                            <div className="mt-1 text-sm font-black" style={textPrimary}>{track.title}</div>
                          </div>
                          <div className="rounded-[12px] px-3 py-2" style={{ background: ui.cardBg, border: 'none' }}>
                            <div className="text-[10px] font-black uppercase tracking-[0.14em]" style={textMuted}>Status</div>
                            <div className="mt-1 text-sm font-black" style={textPrimary}>{value > 0 ? 'Active' : 'Just starting'}</div>
                          </div>
                        </div>

                        <div className="mt-4">
                          <div
                            className="h-2.5 overflow-hidden rounded-full"
                            style={{
                              background: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(91,71,33,0.10)',
                              border: `1px solid ${ui.border}`,
                            }}
                          >
                            <div className="h-full rounded-full" style={{ width: `${fill}%`, background: tone.bar }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })() : null}
            </div>
          </div>
        </div>
    </RoddoPageShell>
  );
};

export default AchievementsView;
